import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { ArrowLeft, Plus, Trash2, Edit2, Check, Save, User, UserPlus, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

export default function AdminSquad() {
  const [squad, setSquad] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Edit / Add Form State
  const [editingId, setEditingId] = useState(null); // null = adding, string = editing ID
  const [name, setName] = useState("");
  const [number, setNumber] = useState("");
  const [positionType, setPositionType] = useState("outfield");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/squad");
      setSquad(r.data || []);
      setError("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setName("");
    setNumber("");
    setPositionType("outfield");
    setError("");
  };

  const startEdit = (player) => {
    setEditingId(player.id);
    setName(player.name);
    setNumber(player.number);
    setPositionType(player.position_type || "outfield");
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !number) {
      setError("Please fill in both Name and Shirt Number fields");
      return;
    }
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const payload = {
        name: name.trim(),
        number: Number(number),
        position_type: positionType,
      };
      if (editingId) {
        await api.put(`/admin/squad/${editingId}`, payload);
        setSuccess("Player updated successfully!");
      } else {
        await api.post("/admin/squad", payload);
        setSuccess("Player added successfully!");
      }
      resetForm();
      await load();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (playerId) => {
    if (!window.confirm("Are you sure you want to remove this player from the squad list?")) return;
    setError("");
    setSuccess("");
    try {
      await api.delete(`/admin/squad/${playerId}`);
      setSuccess("Player removed successfully!");
      await load();
      setTimeout(() => setSuccess(""), 3000);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[var(--brand-ink)]">
      <Header />
      
      <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <Link
              to="/admin"
              className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--brand-body)] uppercase tracking-wider hover:text-[var(--brand-ink)] mb-1"
            >
              <ArrowLeft size={12} /> Back to dashboard
            </Link>
            <h1 className="font-display text-2xl font-bold text-[var(--brand-ink)]">
              Squad List Manager
            </h1>
            <p className="mt-1 text-sm text-[var(--brand-body)]">
              Add, edit, or remove players in the Coventry City squad list used by the Lineup Builder.
            </p>
          </div>
        </div>

        {/* Form panel */}
        <form onSubmit={handleSubmit} className="mb-8 rounded-md border border-black/10 bg-white p-5 shadow-sm">
          <h2 className="font-display text-lg font-bold mb-4 flex items-center gap-2">
            {editingId ? <Edit2 size={16} className="text-[#6CABDD]" /> : <UserPlus size={16} className="text-[#6CABDD]" />}
            {editingId ? "Edit Player Details" : "Add New Squad Player"}
          </h2>

          {error ? (
            <p className="mb-4 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm text-[var(--brand-ink)]">
              {error}
            </p>
          ) : null}
          {success ? (
            <p className="mb-4 rounded-sm border-l-2 border-emerald-500 bg-emerald-500/5 px-3 py-2 text-sm text-emerald-700 font-bold">
              {success}
            </p>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <label className="block label-kicker text-[var(--brand-body)]">Player Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Ben Sheaf"
                className="mt-1 block w-full rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
              />
            </div>
            <div>
              <label className="block label-kicker text-[var(--brand-body)]">Shirt Number</label>
              <input
                type="number"
                min={1}
                max={99}
                value={number}
                onChange={(e) => setNumber(e.target.value)}
                placeholder="e.g. 14"
                className="mt-1 block w-full rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
              />
            </div>
            <div>
              <label className="block label-kicker text-[var(--brand-body)]">Position Type</label>
              <select
                value={positionType}
                onChange={(e) => setPositionType(e.target.value)}
                className="mt-1 block w-full rounded-sm border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
              >
                <option value="outfield">Outfield Player</option>
                <option value="goalkeeper">Goalkeeper</option>
              </select>
            </div>
          </div>

          <div className="mt-5 flex items-center justify-between border-t border-black/5 pt-4">
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-1.5 rounded-md bg-[#0A0F17] px-4 py-2 font-display text-sm font-bold uppercase tracking-wider text-white hover:bg-black disabled:opacity-40"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {editingId ? "Save changes" : "Add Player"}
            </button>
            {editingId ? (
              <button
                type="button"
                onClick={resetForm}
                className="label-kicker text-[var(--brand-body)] underline underline-offset-4 hover:text-[var(--brand-ink)]"
              >
                Cancel edit
              </button>
            ) : null}
          </div>
        </form>

        {/* Squad list */}
        <div className="rounded-md border border-black/10 bg-white p-5 shadow-sm">
          <h2 className="font-display text-lg font-bold mb-4 flex items-center gap-2">
            <User size={16} className="text-[#6CABDD]" />
            Active Squad list
          </h2>

          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((id) => (
                <div key={id} className="h-16 animate-pulse rounded-md border border-black/10 bg-[#F8FAFC]" />
              ))}
            </div>
          ) : squad.length === 0 ? (
            <p className="p-8 text-center text-sm text-[var(--brand-body)] italic">No squad players configured. Initial seeds will load on restart.</p>
          ) : (
            <ul className="divide-y divide-black/5">
              {squad.map((player) => (
                <li key={player.id} className="py-4 flex items-center justify-between gap-4 first:pt-0 last:pb-0">
                  <div className="flex items-center gap-3">
                    <span className={`h-2.5 w-2.5 rounded-full ${player.position_type === "goalkeeper" ? "bg-emerald-500" : "bg-sky-400"}`} />
                    <div>
                      <p className="font-display font-bold text-sm text-[var(--brand-ink)]">
                        {player.name}
                      </p>
                      <p className="mt-0.5 text-xs text-[var(--brand-body)]">
                        {player.position_type === "goalkeeper" ? "Goalkeeper" : "Outfield Player"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-display font-black text-sm text-[var(--brand-body)] mr-4">
                      #{player.number}
                    </span>
                    <button
                      type="button"
                      onClick={() => startEdit(player)}
                      className="inline-flex h-8 items-center gap-1 rounded-md border border-black/10 px-3 text-xs font-semibold text-[var(--brand-ink)] hover:border-[#0A0F17] hover:bg-black/5"
                    >
                      <Edit2 size={10} /> Edit
                    </button>
                    <button
                      type="button"
                      onClick={() => remove(player.id)}
                      className="inline-flex h-8 items-center gap-1 rounded-md border border-border px-3 text-xs font-semibold text-[#E63946] hover:bg-[#E63946] hover:text-white"
                    >
                      <Trash2 size={10} /> Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
